/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React, { useMemo, useState } from 'react';
import { playUiSfx } from '../utils/uiAudio';
import { useGameApp } from '../hooks/useGameApp';
import { buildGameController } from '../hooks/useGameController';
import ConnectedIsoMap from './ConnectedIsoMap';
import UIOverlay from './UIOverlay';
import EnhancedObjectivesPanel from './ui/EnhancedObjectivesPanel';
import StartScreen from './StartScreen';
import LandingPage from './LandingPage';
import MissionPage from './MissionPage';
import HowItWorksPage from './HowItWorksPage';

const GameShell: React.FC = () => {
  const app = useGameApp();
  const c = useMemo(() => buildGameController(app), [app]);
  const [hoveredTile, setHoveredTile] = useState<{ x: number; y: number } | null>(null);

  const { navigation, session, world, build, narrative, economy, policy, ui } = c;

  return (
    <div
      className={`relative w-screen selection:bg-transparent selection:text-transparent ${
        navigation.showLanding
          ? 'min-h-dvh w-full'
          : !session.gameStarted
            ? 'h-dvh overflow-hidden'
            : 'h-dvh overflow-hidden'
      }`}
    >
      {navigation.showLanding ? (
        <div className="min-h-screen z-[100] bg-black">
          {navigation.currentPage === 'landing' && (
            <LandingPage
              onEnter={() => {
                playUiSfx('click');
                navigation.setShowLanding(false);
              }}
              onNavigateToMission={() => {
                playUiSfx('click');
                navigation.setCurrentPage('mission');
              }}
              onNavigateToHowItWorks={() => {
                playUiSfx('click');
                navigation.setCurrentPage('howItWorks');
              }}
            />
          )}
          {navigation.currentPage === 'mission' && (
            <MissionPage
              onBack={() => {
                playUiSfx('click');
                navigation.setCurrentPage('landing');
              }}
            />
          )}
          {navigation.currentPage === 'howItWorks' && (
            <HowItWorksPage
              onBack={() => {
                playUiSfx('click');
                navigation.setCurrentPage('landing');
              }}
            />
          )}
        </div>
      ) : (
        <>
          <ConnectedIsoMap
            onTileClick={build.handleTileClick}
            hoveredTool={build.selectedTool}
            selectedTile={build.selectedTile}
            onHoverTileChange={setHoveredTile}
            activeLayer={world.activeLayer}
            layerVisibility={world.layerVisibility}
            getPlacementError={build.getPlacementError}
          />

          {!session.gameStarted && session.gameStatus === 'playing' && (
            <StartScreen
              onStart={session.handleStart}
              onResume={session.handleResume}
              hasSave={session.savedGameAvailable}
            />
          )}

          {session.gameStarted && (
            <UIOverlay
              stats={world.stats}
              deltas={world.deltas}
              selectedTool={build.selectedTool}
              onSelectTool={(t) => {
                build.setSelectedTool(t);
                build.setSelectedTile(null);
              }}
              currentGoal={narrative.currentGoal}
              newsFeed={narrative.newsFeed}
              onClaimReward={narrative.handleClaimReward}
              isGeneratingGoal={narrative.isGeneratingGoal}
              aiEnabled={narrative.aiEnabled}
              currentStory={narrative.currentStory}
              selectedTile={build.selectedTileData}
              onUpgrade={() => build.handleUpgrade(build.hasJumpGate)}
              onDelete={build.handleDelete}
              onRequestBriefing={() => narrative.triggerStory()}
              onTakeLoan={economy.handleTakeLoan}
              onRepayLoan={economy.handleRepayLoan}
              onPitchInvestors={economy.handlePitchInvestors}
              milestones={world.milestones}
              onLaunchProduct={economy.handleLaunchProduct}
              grid={world.grid}
              tickNumber={world.tickNumber}
              gameStatus={session.gameStatus}
              activeEvent={narrative.activeEvent}
              onEventAck={() => narrative.setActiveEvent(null)}
              onRestart={session.handleSoftReset}
              onRetryScenario={session.handleRetryScenario}
              onSaveNow={session.handleSaveNow}
              activeScenario={narrative.activeScenario}
              scenarioObjectives={narrative.scenarioObjectives}
              activeQuiz={narrative.activeQuiz}
              onQuizAnswer={() => narrative.setActiveQuiz(null)}
              onRequestGoal={narrative.fetchNewGoal}
              onSOCAction={ui.handleSOCAction}
              research={world.research}
              onStartResearch={economy.handleStartResearch}
              activeModal={ui.activeModal}
              onSetActiveModal={ui.setActiveModal}
              onExtend={session.handleExtend}
              gameSpeed={world.gameSpeed}
              setGameSpeed={world.setGameSpeed}
              onAskAICoach={narrative.handleAskAICoach}
              onAutoSolve={narrative.handleAutoSolve}
              simBreakdown={world.simBreakdown}
              showPerformanceMonitor={ui.showPerformanceMonitor}
              onTogglePerformanceMonitor={ui.togglePerformanceMonitor}
              orgManagementOpen={ui.orgManagementOpen}
              onSetOrgManagementOpen={ui.setOrgManagementOpen}
              onToggleEnhancedObjectives={ui.toggleEnhancedObjectives}
              policies={policy.policies}
              policyChallenges={policy.policyChallenges}
              policyMilestones={policy.policyMilestones}
              activePolicy={policy.activePolicy}
              policyEffects={policy.policyEffects}
              onCreatePolicy={policy.handleCreatePolicy}
              onSubmitPolicyForReview={policy.handleSubmitPolicyForReview}
              onApprovePolicy={policy.handleApprovePolicy}
              onImplementPolicy={policy.handleImplementPolicy}
              onEnforcePolicy={policy.handleEnforcePolicy}
              onActivateChallenge={policy.handleActivateChallenge}
              onPolicyAudit={policy.handlePolicyAudit}
              setActivePolicy={policy.setActivePolicy}
              layerManager={world.layerManager}
              hoveredTile={hoveredTile}
            />
          )}

          {ui.showEnhancedObjectives && (
            <EnhancedObjectivesPanel onClose={ui.toggleEnhancedObjectives} />
          )}
        </>
      )}
    </div>
  );
};

export default GameShell;